import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/auth.context";
import { AnimatePresence, motion } from "framer-motion";

import {
  Shield, Plus, Search, Filter, Download, ChevronLeft,
  MoreVertical, Eye, Edit3, Copy, Users, ToggleLeft, ToggleRight,
  Trash2, LoaderCircle, X, Check, ChevronDown, ArrowUpDown,
  LayoutDashboard, Briefcase, BarChart3, Settings,
  AlertTriangle, Save, UserPlus, UserMinus, UserCheck,
  CheckCheck, Minus, Lock, Key, Gauge, PiggyBank, HandCoins,
  Receipt, CalendarDays, Bot, ScrollText, FileSpreadsheet,
  ShieldCheck, RefreshCw, Columns3, ChevronRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { PERMISSION_CATEGORIES } from "./constants";
import type { ModuleDef } from "./constants";
import type { PermissionsMap } from "./helpers";
import { normalizePermissions, denormalizePermissions } from "./helpers";
import {
  countSelected, countTotal, countModuleActions, countModuleEnabled,
  isModuleAllSelected, isModuleSomeSelected,
  toggleModuleAction, toggleModuleAll,
  selectAllPermissions, clearAllPermissions,
  applyDependencies, formatDate, formatDateTime, getAccessLevel,
} from "./helpers";

/* ─────────── Constants ─────────── */

const PAGE_SIZE = 10;
const GREEN = "#009A44";
const GREEN_HOVER = "#007a35";

/* ─────────── Status Badge ─────────── */

function RoleStatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    active: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500", label: "Active" },
    inactive: { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400", label: "Inactive" },
    draft: { bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-400", label: "Draft" },
  };
  const s = styles[status] || styles.inactive;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

/* ─────────── Permission Checkbox Grid ─────────── */

function PermissionCheckbox({
  moduleId, actionId, actionLabel, checked, disabled, onChange,
}: {
  moduleId: string; actionId: string; actionLabel: string;
  checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 cursor-pointer transition-colors group">
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(val) => onChange(!!val)}
        className="data-[state=checked]:bg-[#009A44] data-[state=checked]:border-[#009A44]"
      />
      <span className={`text-xs font-medium transition-colors ${disabled ? "text-gray-300" : "text-gray-600 group-hover:text-gray-900"}`}>
        {actionLabel}
      </span>
    </label>
  );
}

/* ─────────── Permission Module Card ─────────── */

function PermissionModuleCard({
  module,
  permissions,
  onToggleAction,
  onToggleModule,
}: {
  module: ModuleDef;
  permissions: PermissionsMap;
  onToggleAction: (moduleId: string, action: string, checked: boolean) => void;
  onToggleModule: (module: ModuleDef, checked: boolean) => void;
}) {
  const allSelected = isModuleAllSelected(permissions, module);
  const someSelected = isModuleSomeSelected(permissions, module);
  const enabled = countModuleEnabled(permissions, module.id);
  const total = module.actions.length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden transition-all hover:shadow-sm">
      {/* Module header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50/60 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <Checkbox
            checked={allSelected ? true : someSelected ? "indeterminate" : false}
            onCheckedChange={(val) => onToggleModule(module, !!val)}
            className="data-[state=checked]:bg-[#009A44] data-[state=checked]:border-[#009A44]"
          />
          <span className="text-sm font-medium text-gray-800">{module.label}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-normal text-gray-400 border-gray-200">
            {enabled}/{total}
          </Badge>
        </div>
        {someSelected && (
          <span className="text-[10px] text-amber-600 font-medium">Partial</span>
        )}
      </div>

      {/* Permission actions grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-0.5 p-2">
        {module.actions.map((action) => {
          const checked = (permissions[module.id] || []).includes(action.id);
          const key = `${module.id}.${action.id}`;
          const disallowed = ["edit", "delete", "approve", "reject", "verify", "manage", "export", "import", "print", "analytics"].includes(action.id);
          const needsView = key !== `${module.id}.view` && disallowed;
          const disabled = needsView && !(permissions[module.id] || []).includes("view");

          return (
            <PermissionCheckbox
              key={action.id}
              moduleId={module.id}
              actionId={action.id}
              actionLabel={action.label}
              checked={checked}
              disabled={disabled}
              onChange={(val) => onToggleAction(module.id, action.id, val)}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ─────────── Permission Category Accordion ─────────── */

function PermissionCategorySection({
  category,
  permissions,
  onToggleAction,
  onToggleModule,
  searchQuery,
}: {
  category: typeof PERMISSION_CATEGORIES[0];
  permissions: PermissionsMap;
  onToggleAction: (moduleId: string, action: string, checked: boolean) => void;
  onToggleModule: (module: ModuleDef, checked: boolean) => void;
  searchQuery: string;
}) {
  const Icon = category.icon;
  const filteredModules = category.modules.filter((m) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.label.toLowerCase().includes(q) ||
      m.id.toLowerCase().includes(q) ||
      m.actions.some((a) => a.label.toLowerCase().includes(q) || a.id.toLowerCase().includes(q))
    );
  });

  if (filteredModules.length === 0) return null;

  const categoryEnabled = category.modules.reduce((s, m) => s + countModuleEnabled(permissions, m.id), 0);
  const categoryTotal = category.modules.reduce((s, m) => s + m.actions.length, 0);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-[#009A44]/10">
            <Icon className="h-3.5 w-3.5 text-[#009A44]" />
          </div>
          <div>
            <span className="text-sm font-semibold text-gray-800">{category.name}</span>
            <span className="text-[10px] text-gray-400 ml-2">
              {categoryEnabled}/{categoryTotal} enabled
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#009A44] transition-all duration-300"
              style={{ width: `${(categoryEnabled / Math.max(categoryTotal, 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {filteredModules.map((module) => (
          <PermissionModuleCard
            key={module.id}
            module={module}
            permissions={permissions}
            onToggleAction={onToggleAction}
            onToggleModule={onToggleModule}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────── Permission Panel ─────────── */

function PermissionPanel({
  permissions,
  onPermissionsChange,
  readOnly = false,
}: {
  permissions: PermissionsMap;
  onPermissionsChange: (perms: PermissionsMap) => void;
  readOnly?: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const totalEnabled = useMemo(() => countSelected(permissions), [permissions]);
  const totalAll = useMemo(() => countTotal(), []);
  const allFullySelected = totalEnabled === totalAll;

  const handleToggleAction = useCallback((moduleId: string, action: string, checked: boolean) => {
    if (readOnly) return;
    let next = toggleModuleAction(permissions, moduleId, action, checked);
    next = applyDependencies(next, moduleId, action, checked);
    onPermissionsChange(next);
  }, [permissions, onPermissionsChange, readOnly]);

  const handleToggleModule = useCallback((module: ModuleDef, checked: boolean) => {
    if (readOnly) return;
    let next = toggleModuleAll(permissions, module, checked);
    if (checked) {
      for (const action of module.actions) {
        next = applyDependencies(next, module.id, action.id, true);
      }
    }
    onPermissionsChange(next);
  }, [permissions, onPermissionsChange, readOnly]);

  const handleSelectAll = useCallback(() => {
    onPermissionsChange(selectAllPermissions());
  }, [onPermissionsChange]);

  const handleClearAll = useCallback(() => {
    onPermissionsChange(clearAllPermissions());
  }, [onPermissionsChange]);

  return (
    <div className="space-y-4">
      {/* Permission toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <Input
              placeholder="Search permissions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-xs w-full sm:w-56 rounded-lg border-gray-200"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-2 text-gray-300 hover:text-gray-500"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          {!readOnly && (
            <>
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="h-7 text-[11px] text-gray-500 hover:text-[#009A44] px-2"
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Select All
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleClearAll}
                  className="h-7 text-[11px] text-gray-500 hover:text-red-500 px-2"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear All
                </Button>
              </div>
            </>
          )}

          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
            <span className="font-semibold text-gray-800">{totalEnabled}</span>
            <span className="text-gray-300">/</span>
            <span>{totalAll}</span>
            <span className="text-gray-400">enabled</span>
          </div>
        </div>
      </div>

      {/* Global permission toggle */}
      {!readOnly && (
        <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-gray-200 bg-white cursor-pointer hover:border-[#009A44]/30 hover:bg-green-50/30 transition-all">
          <Checkbox
            checked={allFullySelected}
            onCheckedChange={(val) => {
              if (val) onPermissionsChange(selectAllPermissions());
              else onPermissionsChange(clearAllPermissions());
            }}
            className="data-[state=checked]:bg-[#009A44] data-[state=checked]:border-[#009A44]"
          />
          <div>
            <span className="text-sm font-medium text-gray-800">Allow All Permissions</span>
            <span className="text-[10px] text-gray-400 ml-2">
              Grants unrestricted access to all modules and actions
            </span>
          </div>
        </label>
      )}

      {/* Permission progress bar */}
      <div className="flex items-center gap-2.5 px-1">
        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-[#009A44] to-[#00c75a]"
            initial={false}
            animate={{ width: `${(totalEnabled / Math.max(totalAll, 1)) * 100}%` }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        </div>
        <span className="text-[11px] text-gray-400 font-medium whitespace-nowrap">
          {Math.round((totalEnabled / Math.max(totalAll, 1)) * 100)}%
        </span>
      </div>

      {/* Permission categories */}
      <div className="space-y-3">
        {PERMISSION_CATEGORIES.map((category) => (
          <PermissionCategorySection
            key={category.name}
            category={category}
            permissions={permissions}
            onToggleAction={handleToggleAction}
            onToggleModule={handleToggleModule}
            searchQuery={searchQuery}
          />
        ))}
      </div>
    </div>
  );
}

/* ─────────── Role Form Dialog ─────────── */

function RoleFormDialog({
  open, onClose, role, onSuccess,
}: {
  open: boolean; onClose: () => void; role?: any; onSuccess: () => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = !!role;
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUnsaved, setShowUnsaved] = useState(false);
  const pendingClose = useRef(false);

  const initialPermissions = useMemo(() => normalizePermissions(role?.permissions), [role]);
  const hasUnsaved = JSON.stringify(permissions) !== JSON.stringify(initialPermissions) ||
    name !== (role?.name || "") ||
    description !== (role?.description || "");

  useEffect(() => {
    if (open) {
      setName(role?.name || "");
      setCode(role?.code || "");
      setDescription(role?.description || "");
      setStatus(role?.status || "active");
      setPermissions(role?.permissions ? normalizePermissions(role.permissions) : {});
      setErrors({});
      pendingClose.current = false;
    }
  }, [open, role]);

  const handleClose = () => {
    if (hasUnsaved) {
      setShowUnsaved(true);
      pendingClose.current = true;
    } else {
      onClose();
    }
  };

  const handleValidate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Role name is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!handleValidate()) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        code: code.trim() || name.trim().toLowerCase().replace(/\s+/g, "_"),
        description: description.trim() || null,
        status,
        permissions: denormalizePermissions(permissions),
        createdById: user?.id,
      };

      if (isEdit) {
        await api.patch(`/roles/${role.id}`, payload);
        toast.success("Role updated successfully");
      } else {
        await api.post("/roles", payload);
        toast.success("Role created successfully");
      }
      queryClient.invalidateQueries(["roles"]);
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
        <DialogContent className="p-0 gap-0 rounded-xl max-w-5xl w-[95vw] max-h-[90vh] flex flex-col">
          {/* Sticky header */}
          <div className="bg-gradient-to-r from-[#009A44] to-[#007a35] rounded-t-xl px-5 py-3.5 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-white/15 rounded-lg p-1.5 shrink-0">
                <ShieldCheck className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-white font-semibold text-sm leading-tight">
                  {isEdit ? "Edit Role" : "Create Role"}
                </h2>
                <p className="text-white/70 text-[11px] mt-0.5 truncate">
                  {isEdit ? "Configure role details and permissions" : "Define a new role and its permissions"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasUnsaved && (
                <span className="flex items-center gap-1 text-amber-200 text-[10px] bg-white/10 px-2 py-1 rounded-md">
                  <AlertTriangle className="h-3 w-3" />
                  Unsaved
                </span>
              )}
              <DialogClose asChild>
                <button className="bg-white/10 hover:bg-white/20 text-white rounded-lg p-1.5 transition-colors">
                  <X className="h-3.5 w-3.5" />
                </button>
              </DialogClose>
            </div>
          </div>

          {/* Body — scrolls, fills remaining space */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-5 space-y-5">
              {/* Role Information */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Shield className="h-3.5 w-3.5 text-[#009A44]" />
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Role Information</h3>
                  </div>
                </div>
                <div className="p-4 space-y-3.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Role Name <span className="text-red-500">*</span></label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Loan Officer"
                        className={`h-9 text-sm ${errors.name ? "border-red-400" : ""}`}
                      />
                      {errors.name && <p className="text-[11px] text-red-500">{errors.name}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Role Code</label>
                      <Input
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        placeholder="Auto-generated if empty"
                        className="h-9 text-sm"
                      />
                      <p className="text-[10px] text-gray-400">Unique identifier, auto-generated from name</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-gray-700">Description</label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe the responsibilities of this role..."
                      className="min-h-[56px] text-sm resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-gray-700">Status</label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-[#009A44]" />
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Permissions</h3>
                  </div>
                </div>
                <div className="p-4">
                  <PermissionPanel
                    permissions={permissions}
                    onPermissionsChange={setPermissions}
                    readOnly={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer — natural bottom */}
          <div className="shrink-0 border-t border-gray-100 bg-white px-5 py-3.5 flex items-center justify-center gap-3 rounded-b-xl">
            {role?.isSystemRole && (
              <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-md">
                System role — limited editing
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="h-9 px-4 text-sm"
            >
              Cancel
            </Button>
            <Button
              disabled={saving || !name.trim() || role?.isSystemRole}
              onClick={handleSubmit}
              size="sm"
              className="h-9 px-5 text-sm bg-[#009A44] hover:bg-[#007a35] text-white"
            >
              {saving && <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {isEdit ? (
                <><Save className="h-3.5 w-3.5 mr-1.5" /> Save Role</>
              ) : (
                <><Plus className="h-3.5 w-3.5 mr-1.5" /> Create Role</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved changes dialog */}
      <AlertDialog open={showUnsaved} onOpenChange={setShowUnsaved}>
        <AlertDialogContent className="rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              You have unsaved changes. Are you sure you want to leave without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg text-sm">Stay Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowUnsaved(false); onClose(); }}
              className="bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ─────────── Role Details Page ─────────── */

function RoleDetailsPage({
  role,
  onBack,
  onRefresh,
}: {
  role: any;
  onBack: () => void;
  onRefresh: () => void;
}) {
  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Back button & breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-gray-500 hover:text-[#009A44] transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Roles
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-800 font-medium">{role.name}</span>
      </div>

      {/* Header card */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#009A44]/10">
                <Shield className="h-6 w-6 text-[#009A44]" />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-lg font-bold text-gray-900">{role.name}</h1>
                  <RoleStatusBadge status={role.status} />
                </div>
                {role.description && (
                  <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <RoleFormDialog
                open={false}
                onClose={() => {}}
                role={role}
                onSuccess={onRefresh}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={onBack}
                className="h-9 text-sm rounded-lg"
              >
                <ChevronLeft className="h-3.5 w-3.5 mr-1.5" />
                Back
              </Button>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
            {[
              { label: "Users Assigned", value: role.usersAssigned || 0, icon: Users, color: "bg-blue-50 text-blue-600" },
              { label: "Permissions", value: role.permissionsCount || 0, icon: Lock, color: "bg-green-50 text-green-600" },
              { label: "Created By", value: role.createdByName || "System", icon: UserCheck, color: "bg-purple-50 text-purple-600" },
              { label: "Updated", value: formatDateTime(role.updatedAt), icon: RefreshCw, color: "bg-orange-50 text-orange-600" },
            ].map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-gray-50/50 border border-gray-100">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${stat.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">{stat.label}</p>
                    <p className="text-sm font-semibold text-gray-800 truncate">{stat.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Permissions section */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/80 px-5 py-3">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#009A44]" />
            <h3 className="text-sm font-semibold text-gray-800">Assigned Permissions</h3>
          </div>
        </div>
        <div className="p-5">
          <PermissionPanel
            permissions={normalizePermissions(role.permissions)}
            onPermissionsChange={() => {}}
            readOnly={true}
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────── Roles List Page ─────────── */

function RolesListPage({
  onViewRole,
  onEditRole,
  onCreate,
}: {
  onViewRole: (role: any) => void;
  onEditRole: (role: any) => void;
  onCreate: () => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [pageIndex, setPageIndex] = useState(0);
  const [sortField, setSortField] = useState<string>("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [editingRole, setEditingRole] = useState<any>(null);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toggle status loading
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // Duplicate loading
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["roles", "list", searchText, statusFilter, pageIndex, sortField, sortDir],
    keepPreviousData: true,
    queryFn: async () => {
      const params: Record<string, any> = {
        page_size: PAGE_SIZE,
        page: pageIndex + 1,
        sort_by: sortField,
        sort_order: sortDir,
      };
      if (searchText) params.search = searchText;
      if (statusFilter !== "all") {
        params.filters = JSON.stringify([{ field: "status", operator: "in", value: [statusFilter] }]);
      }
      const { data: res } = await api.get("/roles", { params });
      return {
        items: (res.results || []).map((e: any) => ({
          ...e,
          permissions: normalizePermissions(e.permissions),
        })),
        totalPages: res.totalPages || 1,
        total: res.total || 0,
      };
    },
  });

  const roles = data?.items || [];
  const totalPages = data?.totalPages || 1;
  const total = data?.total || 0;

  const handleEdit = (role: any) => {
    setEditingRole(role);
    setEditFormOpen(true);
  };

  const handleView = (role: any) => {
    onViewRole(role);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/roles/${deleteTarget.id}`);
      toast.success(`Role "${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete role");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleStatus = async (role: any) => {
    setTogglingId(role.id);
    try {
      await api.patch(`/roles/${role.id}/toggle-status`);
      const newStatus = role.status === "active" ? "inactive" : "active";
      toast.success(`Role "${role.name}" ${newStatus === "active" ? "activated" : "deactivated"}`);
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to toggle status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDuplicate = async (role: any) => {
    setDuplicatingId(role.id);
    try {
      await api.post(`/roles/${role.id}/duplicate`);
      toast.success(`Role "${role.name}" duplicated`);
      refetch();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to duplicate role");
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === roles.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(roles.map((r: any) => r.id)));
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortHeader = ({ field, label }: { field: string; label: string }) => (
    <button
      onClick={() => handleSort(field)}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-800 transition-colors"
    >
      {label}
      <ArrowUpDown className={`h-3 w-3 ${sortField === field ? "text-[#009A44]" : "text-gray-300"}`} />
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Roles & Permissions</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage system roles, configure permissions, and assign users
          </p>
        </div>
        <Button
          onClick={onCreate}
          size="sm"
          className="h-9 bg-[#009A44] hover:bg-[#007a35] text-white rounded-lg shadow-sm"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Create Role
        </Button>
      </div>

      {/* Filters toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search roles..."
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPageIndex(0); }}
            className="pl-9 h-9 text-sm rounded-lg border-gray-200 w-full"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => { setStatusFilter(v); setPageIndex(0); }}
        >
          <SelectTrigger className="h-9 text-sm w-36 rounded-lg border-gray-200">
            <Filter className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2 ml-auto">
          {selectedRows.size > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
              {selectedRows.size} selected
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            className="h-9 text-sm rounded-lg border-gray-200"
            disabled={isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const csv = [["Name","Description","Users","Permissions","Status","Updated","Created"]];
              roles.forEach((r: any) => {
                csv.push([r.name, r.description || "", String(r.usersAssigned || 0), String(r.permissionsCount || 0), r.status, formatDate(r.updatedAt), formatDate(r.createdAt)]);
              });
              const blob = new Blob([csv.map(r => r.join(",")).join("\n")], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = "roles.csv"; a.click();
              URL.revokeObjectURL(url);
            }}
            className="h-9 text-sm rounded-lg border-gray-200"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={roles.length > 0 && selectedRows.size === roles.length}
                      onCheckedChange={handleSelectAll}
                      className="data-[state=checked]:bg-[#009A44] data-[state=checked]:border-[#009A44]"
                    />
                  </th>
                  <th className="px-3 py-3 text-left"><SortHeader field="name" label="Role Name" /></th>
                  <th className="px-3 py-3 text-left"><span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Description</span></th>
                  <th className="px-3 py-3 text-center"><span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Users</span></th>
                  <th className="px-3 py-3 text-center"><span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Permissions</span></th>
                  <th className="px-3 py-3 text-center"><SortHeader field="status" label="Status" /></th>
                  <th className="px-3 py-3 text-left"><SortHeader field="updatedAt" label="Updated" /></th>
                  <th className="px-3 py-3 text-left"><SortHeader field="createdAt" label="Created" /></th>
                  <th className="px-3 py-3 text-right w-14"><span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <Shield className="h-10 w-10 text-gray-300" />
                        <p className="text-sm text-gray-500">No roles found</p>
                        <Button
                          size="sm"
                          onClick={onCreate}
                          className="bg-[#009A44] hover:bg-[#007a35] text-white h-8 text-xs rounded-lg"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" /> Create Role
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : roles.map((role: any, idx: number) => (
                  <motion.tr
                    key={role.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03, duration: 0.2 }}
                    className={`border-b border-gray-50 transition-colors hover:bg-[#009A44]/[0.02] ${idx % 2 === 1 ? "bg-gray-50/30" : ""}`}
                  >
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selectedRows.has(role.id)}
                        onCheckedChange={() => handleSelectRow(role.id)}
                        className="data-[state=checked]:bg-[#009A44] data-[state=checked]:border-[#009A44]"
                      />
                    </td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => handleView(role)}
                        className="font-medium text-sm text-gray-800 hover:text-[#009A44] transition-colors text-left"
                      >
                        {role.name}
                      </button>
                    </td>
                    <td className="px-3 py-3 max-w-[200px]">
                      <span className="text-sm text-gray-500 truncate block">{role.description || "\u2014"}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold cursor-default">
                              {role.usersAssigned || 0}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            Users assigned to this role
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center justify-center min-w-[26px] h-6 px-1.5 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold">
                        {role.permissionsCount || 0}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <RoleStatusBadge status={role.status} />
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-500">{formatDate(role.updatedAt)}</span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs text-gray-500">{formatDate(role.createdAt)}</span>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 rounded-lg hover:bg-gray-100">
                            <MoreVertical className="h-4 w-4 text-gray-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44 rounded-lg">
                          <DropdownMenuLabel className="text-[11px] text-gray-400 font-normal px-2 py-1.5">Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleView(role)} className="text-xs py-1.5 px-2">
                            <Eye className="h-3.5 w-3.5 mr-2 text-gray-400" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(role)} className="text-xs py-1.5 px-2">
                            <Edit3 className="h-3.5 w-3.5 mr-2 text-gray-400" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(role)} disabled={duplicatingId === role.id} className="text-xs py-1.5 px-2">
                            {duplicatingId === role.id ? (
                              <LoaderCircle className="h-3.5 w-3.5 mr-2 animate-spin" />
                            ) : (
                              <Copy className="h-3.5 w-3.5 mr-2 text-gray-400" />
                            )}
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleToggleStatus(role)}
                            disabled={togglingId === role.id}
                            className="text-xs py-1.5 px-2"
                          >
                            {togglingId === role.id ? (
                              <LoaderCircle className="h-3.5 w-3.5 mr-2 animate-spin" />
                            ) : role.status === "active" ? (
                              <ToggleLeft className="h-3.5 w-3.5 mr-2 text-gray-400" />
                            ) : (
                              <ToggleRight className="h-3.5 w-3.5 mr-2 text-gray-400" />
                            )}
                            {role.status === "active" ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteTarget(role)}
                            className="text-xs py-1.5 px-2 text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-500">
              Showing <span className="font-medium text-gray-700">{pageIndex * PAGE_SIZE + 1}</span> to{" "}
              <span className="font-medium text-gray-700">{Math.min((pageIndex + 1) * PAGE_SIZE, total)}</span> of{" "}
              <span className="font-medium text-gray-700">{total}</span> roles
            </p>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                disabled={pageIndex <= 0}
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                className="h-8 text-xs px-3 rounded-lg border-gray-200"
              >
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const start = Math.max(0, Math.min(pageIndex - 2, totalPages - 5));
                  const p = start + i;
                  if (p >= totalPages) return null;
                  return (
                    <Button
                      key={p}
                      size="sm"
                      variant={p === pageIndex ? "default" : "outline"}
                      onClick={() => setPageIndex(p)}
                      className={`h-8 min-w-[32px] text-xs rounded-lg ${
                        p === pageIndex
                          ? "bg-[#009A44] hover:bg-[#007a35] text-white"
                          : "border-gray-200"
                      }`}
                    >
                      {p + 1}
                    </Button>
                  );
                })}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={pageIndex >= totalPages - 1}
                onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                className="h-8 text-xs px-3 rounded-lg border-gray-200"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Role
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              Are you sure you want to delete <span className="font-semibold text-gray-700">{deleteTarget?.name}</span>?
              {deleteTarget?.usersAssigned > 0 && (
                <span className="block mt-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg text-xs">
                  This role has {deleteTarget.usersAssigned} active user(s) assigned. Move them to another role first.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg text-sm" disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading || deleteTarget?.usersAssigned > 0}
              className="bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {deleteLoading && <LoaderCircle className="h-4 w-4 mr-1.5 animate-spin" />}
              Delete Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Role form dialog (edit only) */}
      {editFormOpen && (
        <RoleFormDialog
          open={editFormOpen}
          onClose={() => { setEditFormOpen(false); setEditingRole(null); }}
          role={editingRole}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
}

/* ─────────── Create Role Page (full-page form) ─────────── */

function CreateRolePage({ onBack, onSuccess }: { onBack: () => void; onSuccess: () => void }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUnsaved, setShowUnsaved] = useState(false);

  const hasUnsaved = !!name || !!description || Object.keys(permissions).length > 0;

  const handleValidate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Role name is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!handleValidate()) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        code: code.trim() || name.trim().toLowerCase().replace(/\s+/g, "_"),
        description: description.trim() || null,
        status,
        permissions: denormalizePermissions(permissions),
        createdById: user?.id,
      };
      await api.post("/roles", payload);
      toast.success("Role created successfully");
      onSuccess();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to create role");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (hasUnsaved) {
      setShowUnsaved(true);
    } else {
      onBack();
    }
  };

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Back */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button onClick={handleBack} className="inline-flex items-center gap-1 text-gray-500 hover:text-[#009A44] transition-colors">
          <ChevronLeft className="h-4 w-4" />
          Roles
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-800 font-medium">New Role</span>
      </div>

      {/* Header */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#009A44]/10">
                <Shield className="h-6 w-6 text-[#009A44]" />
              </div>
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h1 className="text-lg font-bold text-gray-900">New Role</h1>
                </div>
                <p className="text-sm text-gray-500 mt-1">Define a new role and configure its permissions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleBack} className="h-9 text-sm rounded-lg">
                <ChevronLeft className="h-3.5 w-3.5 mr-1.5" /> Back
              </Button>
              <Button
                disabled={saving || !name.trim()}
                onClick={handleSubmit}
                size="sm"
                className="h-9 px-5 text-sm bg-[#009A44] hover:bg-[#007a35] text-white"
              >
                {saving && <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                <Save className="h-3.5 w-3.5 mr-1.5" /> Save Role
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Role Information */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Shield className="h-3.5 w-3.5 text-[#009A44]" />
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Role Information</h3>
          </div>
        </div>
        <div className="p-4 space-y-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-700">Role Name <span className="text-red-500">*</span></label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Loan Officer"
                className={`h-9 text-sm ${errors.name ? "border-red-400" : ""}`}
              />
              {errors.name && <p className="text-[11px] text-red-500">{errors.name}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-700">Role Code</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Auto-generated if empty"
                className="h-9 text-sm"
              />
              <p className="text-[10px] text-gray-400">Unique identifier, auto-generated from name</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-700">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the responsibilities of this role..."
              className="min-h-[56px] text-sm resize-none"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-700">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Permissions */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Lock className="h-3.5 w-3.5 text-[#009A44]" />
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Permissions</h3>
          </div>
        </div>
        <div className="p-4">
          <PermissionPanel
            permissions={permissions}
            onPermissionsChange={setPermissions}
            readOnly={false}
          />
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="sticky bottom-4 z-20 bg-white border border-gray-200 rounded-xl shadow-lg px-5 py-3.5 flex items-center justify-center gap-3">
        <Button variant="outline" size="sm" onClick={handleBack} className="h-9 px-4 text-sm">
          Cancel
        </Button>
        <Button
          disabled={saving || !name.trim()}
          onClick={handleSubmit}
          size="sm"
          className="h-9 px-5 text-sm bg-[#009A44] hover:bg-[#007a35] text-white"
        >
          {saving && <LoaderCircle className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          <Save className="h-3.5 w-3.5 mr-1.5" /> Create Role
        </Button>
      </div>

      {/* Unsaved changes dialog */}
      <AlertDialog open={showUnsaved} onOpenChange={setShowUnsaved}>
        <AlertDialogContent className="rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unsaved Changes
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-500">
              You have unsaved changes. Are you sure you want to leave without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg text-sm">Stay Editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowUnsaved(false); onBack(); }}
              className="bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm"
            >
              Leave Without Saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ─────────── Main Entry ─────────── */

export default function RolesAndPermissions() {
  const queryClient = useQueryClient();
  const [viewingRole, setViewingRole] = useState<any>(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateSuccess = () => {
    setIsCreating(false);
    queryClient.invalidateQueries(["roles"]);
  };

  if (isCreating) {
    return (
      <div className="px-2 sm:px-4 py-4">
        <CreateRolePage
          onBack={() => setIsCreating(false)}
          onSuccess={handleCreateSuccess}
        />
      </div>
    );
  }

  if (viewingRole) {
    return (
      <div className="px-2 sm:px-4 py-4">
        <RoleDetailsPage
          role={viewingRole}
          onBack={() => setViewingRole(null)}
          onRefresh={() => {
            api.get(`/roles/${viewingRole.id}`).then(({ data }) => {
              setViewingRole(data.data);
            });
          }}
        />
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 py-4">
      <RolesListPage
        onViewRole={setViewingRole}
        onEditRole={(role) => setViewingRole(role)}
        onCreate={() => setIsCreating(true)}
      />
    </div>
  );
}
